import { useMemo } from "react";
import { X } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { kpisOf, dailySeries, mean, fmtN, fmtPct, C, SITE_COLORS } from "./icUtils";

const ASSESS_COLORS = { Standard: C.green, "Below Standard": C.yellow, Fatal: C.red };
const RATING_COLORS = { Standard: C.green, "Non-Critical": C.yellow, Critical: C.red };
const tooltipStyle = { background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" };

export default function AgentDrawer({ data, cisco, onClose }) {
  const agent = data.agents[cisco];
  const rows = useMemo(() => ({
    voc: data.voc.filter((r) => r.c === cisco),
    chat: data.chat.filter((r) => r.c === cisco),
    pmt: data.pmt.filter((r) => r.c === cisco).sort((a, b) => ((a.d || "") < (b.d || "") ? 1 : -1)),
    qsl: data.qsl.filter((r) => r.c === cisco).sort((a, b) => ((a.d || "") < (b.d || "") ? 1 : -1)),
  }), [data, cisco]);
  const k = useMemo(() => kpisOf(rows), [rows]);
  const ad = data.adherence[cisco];

  const ahtSeries = useMemo(() => {
    const by = {};
    rows.chat.forEach((r) => {
      const b = (by[r.d] = by[r.d] || { d: r.d.slice(5), WA: [], LC: [] });
      if (r.aht != null) b[r.ch].push(r.aht);
    });
    return Object.keys(by).sort().map((d) => ({ d: by[d].d, WA: mean(by[d].WA), LC: mean(by[d].LC) }));
  }, [rows.chat]);
  const vocSeries = useMemo(() => dailySeries(rows.voc, (rs) => {
    const n = rs.reduce((a, r) => a + r.n, 0);
    return n ? (100 * rs.reduce((a, r) => a + r.t2b, 0)) / n : null;
  }).map((p) => ({ d: p.d.slice(5), v: p.v })), [rows.voc]);

  const chips = [
    { label: "VOC", v: fmtPct(k.voc) }, { label: "Surveys", v: k.surveys || "—" },
    { label: "AHT WA", v: k.ahtWA != null ? `${fmtN(k.ahtWA, 0)}s` : "—" }, { label: "AHT LC", v: k.ahtLC != null ? `${fmtN(k.ahtLC, 0)}s` : "—" },
    { label: "PMT", v: fmtPct(k.pmt) }, { label: "Evals", v: k.evals || "—" }, { label: "Criticals", v: k.evals ? k.crit : "—" },
    { label: "QSL", v: fmtPct(k.qsl) }, { label: "Fatals", v: k.audits ? k.fatals : "—" },
    { label: "Adherence", v: fmtPct(ad?.adh) }, { label: "Crit-Err Avg", v: fmtN(data.critical[cisco], 1) },
  ];

  if (!agent) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ic-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-thborder/15 bg-appbg shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-thborder/10 bg-appbg/95 px-6 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-bold text-thstrong">{agent.name || `Agent ${cisco}`}</h2>
              <p className="font-mono-hud mt-1 text-[10px] uppercase tracking-[0.2em] text-thdim">
                Cisco {cisco} · <span style={{ color: SITE_COLORS[agent.site] }}>{agent.site}</span> · TL {agent.tl || "—"} · {agent.lob || "—"}
              </p>
              <p className="font-mono-hud mt-0.5 text-[9px] uppercase tracking-[0.15em] text-thdim">Full history shown (not limited by the date filter)</p>
            </div>
            <button data-testid="ic-drawer-close" onClick={onClose} className="rounded-md p-2 text-thmute hover:bg-thhover/10 hover:text-thstrong"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="flex flex-wrap gap-2">
            {chips.map((c) => (
              <div key={c.label} className="rounded-xl border border-thborder/10 bg-thinput px-3 py-2">
                <p className="font-mono-hud text-[9px] uppercase tracking-[0.12em] text-thdim">{c.label}</p>
                <p className="font-mono-hud text-sm font-semibold text-thcyan">{c.v}</p>
              </div>
            ))}
          </div>

          {ahtSeries.length > 1 && (
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Daily AHT (sec)</p>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <LineChart data={ahtSeries} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                    <XAxis dataKey="d" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="WA" stroke={C.cyan} strokeWidth={2} dot={false} connectNulls name="WhatsApp" />
                    <Line type="monotone" dataKey="LC" stroke={C.purple} strokeWidth={2} dot={false} connectNulls name="Live Chat" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {vocSeries.length > 0 && (
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Daily VOC Top-2-Box %</p>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <BarChart data={vocSeries} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                    <XAxis dataKey="d" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} domain={[0, 100]} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    <Bar dataKey="v" fill={C.green} radius={[2, 2, 0, 0]} name="Top-2-Box %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div>
            <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">PMT Evaluations ({rows.pmt.length})</p>
            {rows.pmt.length === 0 ? <p className="text-xs text-thdim">No internal evaluations.</p> : (
              <div className="space-y-2">
                {rows.pmt.map((p, i) => (
                  <div key={i} className="rounded-xl border border-thborder/10 bg-thinput p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-mono-hud text-xs text-thmute">{p.d || "—"} · {p.skill}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-hud text-sm font-semibold" style={{ color: p.score != null && p.score >= 80 ? C.green : C.red }}>{fmtPct(p.score)}</span>
                        <span className="rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase" style={{ background: `${RATING_COLORS[p.rating] || C.grey}22`, color: RATING_COLORS[p.rating] || C.grey }}>{p.rating}</span>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {data.pmt_sub_names.map((name, j) => {
                        const v = p.subs[j];
                        return (
                          <span key={j} title={name} className={`rounded px-1.5 py-0.5 font-mono-hud text-[9px] ${v == null ? "bg-thhover/10 text-thdim" : v ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F43F5E]/15 text-[#F43F5E]"}`}>
                            {name.length > 22 ? `${name.slice(0, 20)}…` : name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pb-6">
            <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">QSL Audits ({rows.qsl.length})</p>
            {rows.qsl.length === 0 ? <p className="text-xs text-thdim">No external audits.</p> : (
              <div className="space-y-2">
                {rows.qsl.map((q, i) => (
                  <div key={i} className="rounded-xl border border-thborder/10 bg-thinput p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-mono-hud text-xs text-thmute">{q.d || "—"}{q.approx && "≈"} · {q.ch === "WA" ? "WhatsApp" : "Live Chat"}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-hud text-sm font-semibold text-thbody">{fmtN(q.score, 0)}</span>
                        <span className="rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase" style={{ background: `${ASSESS_COLORS[q.assessment]}22`, color: ASSESS_COLORS[q.assessment] }}>{q.assessment}</span>
                      </div>
                    </div>
                    {q.tag && <p className="mt-1 text-[11px] text-thdim">{q.tag}</p>}
                    {q.errors.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {q.errors.map((e, j) => (
                          <span key={j} className="rounded bg-[#F43F5E]/10 px-1.5 py-0.5 text-[10px] text-[#F43F5E]">{e.cat}{e.err ? `: ${e.err}` : ""}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
